import { AccountConfig } from '@/interfaces/AccountConfig';
import { Department } from '@/interfaces/Department';
import { CurrencyType } from '@/interfaces/Money';
import { StateProvince } from '@/interfaces/PCard';
import { ServerResponse } from '@/interfaces/Server';
import { ExpRptUDF, UDFValue } from '@/interfaces/UDF';
import { logger } from '@/util/logger';
import { parseData, post } from './common';
import {Employee} from '@/interfaces/Employee';
import { ExpType, ExpTypes } from '@/interfaces/ExpType';

export interface RequestOptions {
    Dept: Department;
    ExpCat: unknown;
    ExpType: ExpType;
    Emp: Employee;
    DynamicFilterPK: number
}

export const defaultHandler = (resp: Response): Promise<unknown> => {
    return resp.json()
        .then((json) => JSON.parse(json.d.Data))
        .catch(logger.error);
};

export const responseHandler = (resp: ServerResponse) => {
    if (resp.Status === 'error' || resp.Status === 'reject') {
        throw new Error(resp.Message);
    } 
    return JSON.parse(resp.Data);
};

export const returnFullResponseHandler = (resp: ServerResponse) => {
    if (resp.Status === 'error') {
        throw new Error(resp.Message);
    } 
    return {...resp, ...{Data: JSON.parse(resp.Data)}};
};

/**
 * 
 * @param {*} cacheKey change cache key to bust GetAccountConfig cache 
 * @returns Promise
 */
export const getAccountConfig = (cacheKey: any = '') => {  
    return post('/InternalAPI/ApplicationAPI.asmx/GetAccountConfig', {
        CacheKey: cacheKey
    })
        .then(defaultHandler) as Promise<Partial<AccountConfig>>;
};

export const getExpTypes = () => {  
    //to do: useExpCats needs to pass in a way to distinguish we are dealing with a SR, setting to true for now
    return post('/InternalAPI/ApplicationAPI.asmx/GetExpTypes')
        .then(defaultHandler) as Promise<ExpTypes[]>;
};

export const getExpenseCategories = (options: Partial<RequestOptions>, excludeSRExpCats: boolean = true) => {  
    //to do: useExpCats needs to pass in a way to distinguish we are dealing with a SR, setting to true for now
    return post('/InternalAPI/ExpenseCategoryAPI.asmx/GetExpCats', {
        Dept: options.Dept || null,
        ExpType: options.ExpType || null,
        ExcludeSRExpCats: excludeSRExpCats,
        EmpIdOverride: options.Emp?.PK || null
    })
        .then(defaultHandler) as Promise<unknown[]>;
};

export const getExpRptUDFRecentSelections = (index, options: Partial<RequestOptions>) => {
    return post('/InternalAPI/UDFAPI.asmx/GetExpRptUDFRecentSelections', {
        Index: index,
        ...options
    })
        .then(defaultHandler) as Promise<ExpRptUDF[]>;
};

export const getTextUDFRecentSelections = (index, options: Partial<RequestOptions>) => {
    return post('/InternalAPI/UDFAPI.asmx/GetTextUDFRecentSelections', {
        Index: index,
        ...options
    })
        .then(defaultHandler) as Promise<UDFValue[]>;
};

export const getExpRptUDFs = (udfIndex, options: Partial<RequestOptions> = {}) => {
    return post('/InternalAPI/UDFAPI.asmx/GetExpRptUDFs', {
        Index: udfIndex,
        Emp: options.Emp || {},

    })
        .then(defaultHandler) as Promise<ExpRptUDF[]>;
};

export const getExpRptUDFsByKeyword = (udfIndex, keyword, options: Partial<RequestOptions> = {}) => {
    return post('/InternalAPI/UDFAPI.asmx/GetExpRptUDFsByKeyword', {
        Index: udfIndex,
        strKeyWord: keyword,
        Emp: options.Emp || {},
        DynamicFilterPK: options.DynamicFilterPK || {}
    })
        .then(defaultHandler) as Promise<ExpRptUDF[]>;
};         

export const getCurrencies = () => {
    //keyword parameter for textbox search
    return post('/InternalAPI/ApplicationAPI.asmx/GetCurrencies', {
        Payload: {}
    })
        .then(defaultHandler) as Promise<CurrencyType[]>;
};

//keyword parameter for textbox search
export const getDepartments = (options: Partial<RequestOptions> = {}) => { 
    return post('/InternalAPI/DepartmentAPI.asmx/GetDepartments', {
        Emp: options.Emp
    })
        .then(defaultHandler) as Promise<Department[]>;
};

export const getDepartmentsByKeyword = (keyword = '', options: Partial<RequestOptions> = {}) => { 
    return post('/InternalAPI/DepartmentAPI.asmx/GetDepartmentsByKeyword', {
        Keyword: keyword,
        Emp: options.Emp
    })
        .then(defaultHandler) as Promise<{ Count: number, Results: Department[] }>;
};

export const getStateProvinces = () => {  
    return post('/InternalAPI/ApplicationAPI.asmx/GetStateProvinceList')
        .then(defaultHandler) as Promise<StateProvince[]>;
};

export const getCompanyPreference = (Key1: string, Key2: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/GetCompanyPreference', { Key1: Key1, Key2: Key2 })
        .then(parseData);
};

export const saveCompanyPreference = (Key1: string, Key2: string, Data: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/SaveCompanyPreference', { Key1: Key1, Key2: Key2, Data: Data })
        .then(parseData);
};

export const deleteCompanyPreference = (Key1: string, Key2: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/DeleteCompanyPreference', { Key1: Key1, Key2: Key2 })
        .then(parseData);
};

export const saveUserPreference = (Key1: string, Data: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/SaveUserPreference', { PreferenceName: Key1, Value: Data })
        .then(parseData);
};

export const deleteUserPreference = (Key1: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/DeleteUserPreference', { PreferenceName: Key1})
        .then(parseData);
};

export const validateReceiptAuthCode = (code: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/ValidateAttachmentAuthCode', { code })
        .then(parseData);
};

export const regenerateReceiptAuthCode = (): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/RegenerateAttachmentAuthCode')
        .then(parseData);
}

export const setSessionReturnURL = (ReturnURL: string): Promise<ServerResponse> => {
    return post('/InternalAPI/ApplicationAPI.asmx/SetReturnURL', { ReturnURL })
        .then(parseData);
}
